import { FC } from 'react';

import { PlaceholderBar } from '../../components/loading-skeleton/index';

// Table-only skeleton — shown inside the tabs card while a tab's query loads, so
// switching tabs does not flash the whole-page SafesPlaceholder.
// Mirrors DesktopDetails (desktop/desktop-details.tsx) pending-conversion columns:
// w-12 checkbox, name, investment, issue date, val cap, discount, floor, maturity, actions.
export const SafesTablePlaceholder: FC<{ rows?: number }> = ({ rows = 3 }) => (
  <div className="w-full overflow-x-auto">
    <table className="h-fit w-full divide-y-[1px] divide-[#F2F2F2] border-b-[1px] border-[#F2F2F2]">
      <colgroup>
        <col className="w-12 shrink-0" span={1} />
      </colgroup>
      <thead>
        <tr className="h-11 bg-gray-50">
          <td className="relative">
            <div className="flex h-full w-full items-center justify-center px-4">
              <PlaceholderBar className="size-4 rounded bg-gray-100" />
            </div>
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-24 rounded bg-gray-100" />
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-20 rounded bg-gray-100" />
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-20 rounded bg-gray-100" />
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-12 rounded bg-gray-100" />
          </td>
          <td className="pl-4">
            <PlaceholderBar className="h-4 w-24 rounded bg-gray-100" />
          </td>
          <td className="w-12" />
        </tr>
      </thead>
      <tbody className="divide-y-[1px] divide-[#F2F2F2] bg-white">
        {Array.from({ length: rows }).map((_, index) => (
          <tr className="h-14" key={index}>
            <td className="relative">
              <div className="flex h-full w-full items-center justify-center px-4">
                <PlaceholderBar className="size-4 rounded bg-gray-100" />
              </div>
            </td>
            <td className="p-4">
              <PlaceholderBar className="h-4 w-32 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-20 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-20 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-12 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-12 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-20 rounded bg-gray-100" />
            </td>
            <td className="w-12 pr-4">
              <PlaceholderBar className="size-5 rounded bg-gray-100" />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const SafesPlaceholder: FC = () => (
  <div className="flex h-full min-h-[86vh] w-full animate-pulse flex-col gap-4 p-1">
    {/* Overview toolbar */}
    <div className="hidden items-center justify-between rounded-md p-4 shadow-sm xlg:flex">
      <div className="flex items-center gap-2">
        <PlaceholderBar className="size-5 rounded bg-gray-100" />
        <PlaceholderBar className="h-5 w-24 rounded bg-gray-100" />
      </div>
      <div className="flex items-center gap-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <PlaceholderBar className="h-9 w-24 rounded bg-gray-100" key={index} />
        ))}
        <PlaceholderBar className="h-9 w-28 rounded bg-gray-100" />
      </div>
    </div>

    {/* Overall status + SAFE recap */}
    <div className="flex h-max w-full items-stretch gap-4 max-lg:flex-col">
      <div className="flex min-h-full w-full flex-col rounded-lg bg-gray-50 p-1">
        <div className="px-4 py-3">
          <PlaceholderBar className="h-3 w-28 rounded bg-gray-100" />
        </div>
        <div className="flex h-full w-full flex-wrap content-start gap-3 rounded-md bg-white p-3 shadow-sm max-lg:flex-col">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              className="flex h-20 flex-grow basis-[40%] flex-col gap-2 rounded-lg bg-gray-50 px-2 py-3"
              key={index}
            >
              <PlaceholderBar className="h-3 w-24 rounded bg-gray-100" />
              <PlaceholderBar className="h-5 w-20 rounded bg-gray-100" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex min-h-full w-full flex-col rounded-lg bg-gray-50 p-1">
        <div className="px-4 py-3">
          <PlaceholderBar className="h-3 w-24 rounded bg-gray-100" />
        </div>
        <div className="flex h-full min-h-[240px] w-full items-end justify-center rounded-md bg-white p-6 shadow-sm">
          <PlaceholderBar className="h-[180px] w-2/3 rounded-t-md bg-gray-100" />
        </div>
      </div>
    </div>

    {/* Tabs + table */}
    <div className="flex h-fit flex-col gap-4 rounded-md p-4 shadow-sm">
      <PlaceholderBar className="h-9 w-48 rounded border border-gray-200 bg-gray-100" />
      <SafesTablePlaceholder />
    </div>
  </div>
);
